import {
  addCalendarDaysInTimeZone,
  formatInTimeZone,
  isValidTimeZone,
  parseDateAndTimeInTimeZone,
} from './timeZone';

export type AppointmentCreateMode = 'single' | 'series' | 'group';
export type AppointmentBatchType = Exclude<AppointmentCreateMode, 'single'>;

export const APPOINTMENT_BATCH_LIMITS = {
  seriesOccurrences: 200,
  weeklyWeeks: 52,
  weeklySlots: 14,
  groupPatients: 30,
} as const;

export interface AppointmentDateRange {
  start: Date;
  end: Date;
}

export interface WeeklyAppointmentSlot {
  weekday: number;
  startTime: string;
  endTime: string;
}

export interface SpecificAppointmentSlot {
  date: string;
  startTime: string;
  endTime: string;
}

export interface AppointmentOccurrence {
  patientId: string;
  start: Date;
  end: Date;
  batchType: AppointmentBatchType | null;
  batchOccurrenceIndex: number | null;
}

interface PersistedAppointmentBatchItem {
  patientId?: string | null;
  appointmentStartDate?: string | null;
  appointmentEndDate?: string | null;
  appointmentTimeZone?: string | null;
  appointmentStatus?: string | null;
  appointmentBatchType?: string | null;
  appointmentBatchOccurrenceIndex?: number | string | null;
}

interface BuildAppointmentOccurrencesInput {
  mode: AppointmentCreateMode;
  patientIds: string[];
  start?: Date;
  end?: Date;
  seriesRanges?: AppointmentDateRange[];
}

const assertValidDateRange = (start: Date, end: Date) => {
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new Error('Datas inválidas.');
  }

  if (end <= start) {
    throw new Error('O fim precisa ser depois do início.');
  }
};

const assertIntegerInRange = (value: number, min: number, max: number, label: string) => {
  if (!Number.isInteger(value) || value < min || value > max) {
    throw new Error(`${label} deve estar entre ${min} e ${max}.`);
  }
};

const normalizeSeriesRanges = (ranges: AppointmentDateRange[]): AppointmentDateRange[] => {
  assertIntegerInRange(
    ranges.length,
    2,
    APPOINTMENT_BATCH_LIMITS.seriesOccurrences,
    'A quantidade de sessões',
  );

  const sortedRanges = ranges
    .map((range) => ({ start: new Date(range.start), end: new Date(range.end) }))
    .sort((first, second) => first.start.getTime() - second.start.getTime());

  sortedRanges.forEach((range) => assertValidDateRange(range.start, range.end));
  for (let index = 1; index < sortedRanges.length; index += 1) {
    if (sortedRanges[index].start < sortedRanges[index - 1].end) {
      throw new Error('Existem sessões duplicadas ou com horários sobrepostos na série.');
    }
  }

  return sortedRanges;
};

export const buildWeeklyAppointmentRanges = ({
  initialWeekDate,
  weeks,
  slots,
  timeZone,
}: {
  initialWeekDate: string;
  weeks: number;
  slots: WeeklyAppointmentSlot[];
  timeZone: string;
}): AppointmentDateRange[] => {
  assertIntegerInRange(weeks, 1, APPOINTMENT_BATCH_LIMITS.weeklyWeeks, 'A quantidade de semanas');
  assertIntegerInRange(slots.length, 1, APPOINTMENT_BATCH_LIMITS.weeklySlots, 'A quantidade de horários semanais');

  if (!isValidTimeZone(timeZone)) {
    throw new Error('Selecione um fuso horário IANA válido para a clínica.');
  }
  const resolvedTimeZone = timeZone.trim();
  // Meio-dia evita datas históricas em que a troca de horário de verão
  // acontecia à meia-noite; o horário real de cada sessão é validado abaixo.
  const anchorDate = parseDateAndTimeInTimeZone(initialWeekDate, '12:00', resolvedTimeZone);
  const anchorWeekday = Number(formatInTimeZone(anchorDate, 'E', resolvedTimeZone));
  const firstMonday = addCalendarDaysInTimeZone(anchorDate, 1 - anchorWeekday, resolvedTimeZone);
  const ranges: AppointmentDateRange[] = [];

  for (let weekIndex = 0; weekIndex < weeks; weekIndex += 1) {
    slots.forEach((slot) => {
      assertIntegerInRange(slot.weekday, 1, 7, 'O dia da semana');
      const occurrenceDate = addCalendarDaysInTimeZone(
        firstMonday,
        (weekIndex * 7) + slot.weekday - 1,
        resolvedTimeZone,
      );
      const dateValue = formatInTimeZone(occurrenceDate, 'YYYY-MM-DD', resolvedTimeZone);
      ranges.push({
        start: parseDateAndTimeInTimeZone(dateValue, slot.startTime, resolvedTimeZone),
        end: parseDateAndTimeInTimeZone(dateValue, slot.endTime, resolvedTimeZone),
      });
    });
  }

  return normalizeSeriesRanges(ranges);
};

export const buildSpecificAppointmentRanges = (
  slots: SpecificAppointmentSlot[],
  timeZone: string,
): AppointmentDateRange[] => normalizeSeriesRanges(
  slots.map((slot) => ({
    start: parseDateAndTimeInTimeZone(slot.date, slot.startTime, timeZone),
    end: parseDateAndTimeInTimeZone(slot.date, slot.endTime, timeZone),
  })),
);

export const buildAppointmentOccurrences = ({
  mode,
  patientIds,
  start,
  end,
  seriesRanges = [],
}: BuildAppointmentOccurrencesInput): AppointmentOccurrence[] => {
  const normalizedPatientIds = patientIds.map((patientId) => patientId.trim()).filter(Boolean);
  if (new Set(normalizedPatientIds).size !== normalizedPatientIds.length) {
    throw new Error('O mesmo paciente não pode aparecer duas vezes no lote.');
  }

  if (mode === 'single') {
    if (normalizedPatientIds.length !== 1) {
      throw new Error('Selecione um paciente para o agendamento.');
    }

    if (!start || !end) {
      throw new Error('Informe o início e o fim do agendamento.');
    }
    assertValidDateRange(start, end);

    return [{
      patientId: normalizedPatientIds[0],
      start: new Date(start),
      end: new Date(end),
      batchType: null,
      batchOccurrenceIndex: null,
    }];
  }

  if (mode === 'series') {
    if (normalizedPatientIds.length !== 1) {
      throw new Error('Selecione um paciente para a série.');
    }

    return normalizeSeriesRanges(seriesRanges).map((range, index) => ({
      patientId: normalizedPatientIds[0],
      start: range.start,
      end: range.end,
      batchType: 'series' as const,
      batchOccurrenceIndex: index + 1,
    }));
  }

  assertIntegerInRange(normalizedPatientIds.length, 2, APPOINTMENT_BATCH_LIMITS.groupPatients, 'A quantidade de pacientes');
  if (!start || !end) {
    throw new Error('Informe o início e o fim do atendimento em grupo.');
  }
  assertValidDateRange(start, end);

  return normalizedPatientIds.map((patientId, index) => ({
    patientId,
    start: new Date(start),
    end: new Date(end),
    batchType: 'group' as const,
    batchOccurrenceIndex: index + 1,
  }));
};

export const createAppointmentBatchId = (): string => {
  if (typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
};

export const matchesCompleteAppointmentBatch = (
  persistedItems: PersistedAppointmentBatchItem[],
  expectedOccurrences: AppointmentOccurrence[],
  expectedTimeZone: string,
): boolean => {
  const persistedByIndex = new Map(
    persistedItems.map((item) => [Number(item.appointmentBatchOccurrenceIndex), item])
  );

  return persistedByIndex.size === expectedOccurrences.length
    && expectedOccurrences.every((occurrence) => {
      const persisted = persistedByIndex.get(Number(occurrence.batchOccurrenceIndex));
      return Boolean(
        persisted
        && persisted.patientId === occurrence.patientId
        && persisted.appointmentStatus === 'scheduled'
        && persisted.appointmentTimeZone === expectedTimeZone
        && persisted.appointmentBatchType === occurrence.batchType
        && persisted.appointmentStartDate
        && new Date(persisted.appointmentStartDate).getTime() === occurrence.start.getTime()
        && persisted.appointmentEndDate
        && new Date(persisted.appointmentEndDate).getTime() === occurrence.end.getTime()
      );
    });
};
